// src/components/SubscriptionTab.jsx
// Displays user's subscription info with upgrade/manage options

import React, { useState, useEffect } from 'react';
import { 
  CreditCard, CheckCircle, AlertCircle, ArrowRight, 
  Zap, Shield, Clock, RefreshCw, ExternalLink 
} from 'lucide-react';
import { 
  getUserSubscription, 
  getUserUsage, 
  openCustomerPortal, 
  PLAN_DETAILS, 
  formatDate 
} from '../utils/subscription';
import './SubscriptionTab.css';

const SubscriptionTab = ({ user }) => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, [user]);

  const loadSubscriptionData = async () => {
    if (!user) return;
    
    setLoading(true);
    
    const [subData, usageData] = await Promise.all([
      getUserSubscription(user.id),
      getUserUsage(user.id),
    ]);
    
    setSubscription(subData);
    setUsage(usageData);
    setLoading(false);
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    
    const portalUrl = await openCustomerPortal(user.id);
    
    if (portalUrl) {
      window.location.href = portalUrl;
    } else {
      alert('Unable to open subscription management. Please try again.');
      setPortalLoading(false);
    }
  };

  const handleUpgrade = () => {
    window.location.href = '/pricing';
  };

  if (loading) {
    return (
      <div className="subscription-loading">
        <RefreshCw className="loading-spinner" />
        <p>Loading subscription...</p>
      </div>
    );
  }

  const plan = subscription?.plan || 'free';
  const planDetails = PLAN_DETAILS[plan] || PLAN_DETAILS.free;
  const isFreePlan = plan === 'free';
  const isCanceling = subscription?.cancel_at_period_end;

  // Calculate usage percentage
  const searchLimit = plan === 'free' ? 100 : plan === 'basic' ? 250 : Infinity;
  const searchCount = usage?.search_count || 0;
  const usagePercent = searchLimit === Infinity ? 0 : Math.min(100, (searchCount / searchLimit) * 100);

  return (
    <div className="subscription-tab">
      {/* Current Plan Card */}
      <div className="plan-card" style={{ borderColor: planDetails.color }}>
        <div className="plan-card-header">
          <div className="plan-badge" style={{ backgroundColor: planDetails.color }}>
            {planDetails.name}
          </div>
          {!isFreePlan && (
            <span className={`plan-status ${subscription?.status}`}>
              {isCanceling ? 'Canceling' : subscription?.status === 'active' ? 'Active' : subscription?.status}
            </span>
          )}
        </div>

        <div className="plan-card-body">
          <div className="plan-features">
            <h3>Your Plan Includes:</h3>
            <ul>
              <li>
                <Zap className="feature-icon" style={{ color: planDetails.color }} />
                <span><strong>{planDetails.searches}</strong> flight searches</span>
              </li>
              <li>
                <Shield className="feature-icon" style={{ color: planDetails.color }} />
                <span><strong>{planDetails.alerts}</strong> price alerts</span>
              </li>
              {planDetails.features.map((feature, idx) => (
                <li key={idx}>
                  <CheckCircle className="feature-icon" style={{ color: planDetails.color }} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Usage Stats */}
          <div className="usage-section">
            <h3>This Month's Usage</h3>
            <div className="usage-stat">
              <div className="usage-label">
                <span>Flight Searches</span>
                <span className="usage-numbers">
                  {searchCount} / {searchLimit === Infinity ? '∞' : searchLimit}
                </span>
              </div>
              <div className="usage-bar">
                <div 
                  className="usage-fill" 
                  style={{ 
                    width: `${usagePercent}%`,
                    backgroundColor: usagePercent > 90 ? '#ef4444' : usagePercent > 70 ? '#f59e0b' : planDetails.color
                  }}
                />
              </div>
            </div>
          </div>

          {/* Billing Info (for paid plans) */}
          {!isFreePlan && subscription?.current_period_end && (
            <div className="billing-info">
              <Clock className="billing-icon" />
              <div className="billing-details">
                {isCanceling ? (
                  <>
                    <span className="billing-label">Plan ends on</span>
                    <span className="billing-date">{formatDate(subscription.current_period_end)}</span>
                  </>
                ) : (
                  <>
                    <span className="billing-label">Next billing date</span>
                    <span className="billing-date">{formatDate(subscription.current_period_end)}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="plan-card-actions">
          {isFreePlan ? (
            <button className="upgrade-btn" onClick={handleUpgrade}>
              <Zap className="btn-icon" />
              Upgrade to Pro
              <ArrowRight className="btn-icon-right" />
            </button>
          ) : (
            <>
              <button 
                className="manage-btn" 
                onClick={handleManageSubscription}
                disabled={portalLoading}
              >
                {portalLoading ? (
                  <RefreshCw className="btn-icon spinning" />
                ) : (
                  <CreditCard className="btn-icon" />
                )}
                {portalLoading ? 'Opening...' : 'Manage Subscription'}
                <ExternalLink className="btn-icon-right" />
              </button>
              {plan !== 'max' && (
                <button className="upgrade-btn secondary" onClick={handleUpgrade}>
                  Upgrade Plan
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Canceling Warning */}
      {isCanceling && (
        <div className="cancel-warning">
          <AlertCircle className="warning-icon" />
          <div className="warning-content">
            <strong>Your subscription is set to cancel</strong>
            <p>You'll have access until {formatDate(subscription.current_period_end)}. After that, you'll be moved to the Free plan.</p>
            <button className="reactivate-btn" onClick={handleManageSubscription}>
              Reactivate Subscription
            </button>
          </div>
        </div>
      )}

      {/* Upgrade Prompt (for Free users) */}
      {isFreePlan && (
        <div className="upgrade-prompt">
          <div className="upgrade-prompt-content">
            <h3>🚀 Unlock More with Pro</h3>
            <p>Get unlimited searches, advanced AI recommendations, priority support, and more.</p>
            <ul className="upgrade-benefits">
              <li>✓ Unlimited flight searches</li>
              <li>✓ 25 price alerts per month</li>
              <li>✓ Advanced AI recommendations</li>
              <li>✓ Priority support</li>
            </ul>
          </div>
          <button className="upgrade-cta" onClick={handleUpgrade}>
            View Plans
            <ArrowRight className="btn-icon-right" />
          </button>
        </div>
      )}

      {/* Help Section */}
      <div className="subscription-help">
        <h4>Need help?</h4>
        <p>
          Contact us at <a href="mailto:support@airgati.com">support@airgati.com</a> for any billing questions.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionTab;
